import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Award, CreditCard, DollarSign, Check, Clock, ChevronRight, Coins, X, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BRAND_COLORS = {
  Amazon: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400', icon: '🛒' },
  Apple: { bg: 'bg-zinc-500/10', border: 'border-zinc-500/20', text: 'text-zinc-300', icon: '🍎' },
  'Uber Eats': { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400', icon: '🚗' },
  Fortnite: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', icon: '🎮' },
  'Honey Pot': { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: '💰' },
};

export default function RewardsPage() {
  const { user, token, refreshUser, API } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pointValueCents, setPointValueCents] = useState(0.25);

  const fetchRewards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rewards`, { headers: { Authorization: `Bearer ${token}` } });
      setRewards(res.data.rewards);
      setUserPoints(res.data.user_points);
      if (res.data.point_value_cents) setPointValueCents(res.data.point_value_cents);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/rewards/my-redemptions`, { headers: { Authorization: `Bearer ${token}` } });
      setRedemptions(res.data);
    } catch (e) { console.error(e); }
  };

  const openReward = (reward) => {
    setSelectedReward(reward);
    setSelectedTier(null);
    setConfirming(false);
    setRedeemResult(null);
  };

  const handleRedeem = async () => {
    if (!selectedReward || selectedTier === null) return;
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/rewards/redeem`, {
        reward_id: selectedReward.id,
        tier_index: selectedTier
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRedeemResult(res.data);
      setUserPoints(res.data.new_points);
      await refreshUser();
    } catch (e) {
      setRedeemResult({ error: e.response?.data?.detail || 'Redemption failed' });
    }
    setRedeeming(false);
  };

  const getBrand = (brand) => BRAND_COLORS[brand] || BRAND_COLORS['Honey Pot'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-amber-500" />
          <h2 className="font-heading font-bold text-base text-white">Rewards</h2>
        </div>
        <button
          data-testid="rewards-history-btn"
          onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
          className="text-[10px] text-amber-500 font-medium flex items-center gap-1 hover:text-amber-400"
        >
          <Clock className="w-3 h-3" />
          {showHistory ? 'Hide History' : 'My Redemptions'}
        </button>
      </div>

      {/* Points Balance */}
      <div className="rounded-xl p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20" data-testid="rewards-balance-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Your Balance</p>
            <p className="text-2xl font-black font-mono text-amber-500" data-testid="rewards-points-balance">{userPoints.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500">= ${(userPoints * pointValueCents / 100).toFixed(2)} value</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Coins className="w-6 h-6 text-amber-500" />
          </div>
        </div>
      </div>

      {/* Redemption History */}
      <AnimatePresence>
        {showHistory && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-2">
              <p className="text-xs font-bold text-zinc-400">Redemption History</p>
              {redemptions.length === 0 ? (
                <p className="text-xs text-zinc-600 py-4 text-center">No redemptions yet</p>
              ) : (
                redemptions.slice(0, 5).map(r => (
                  <div key={r.id} className="glass-card rounded-lg px-3 py-2.5 flex items-center gap-3" data-testid={`redemption-${r.id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${getBrand(r.reward_brand).bg}`}>
                      {getBrand(r.reward_brand).icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate">{r.reward_name} ({r.tier_label})</p>
                      <p className="text-[10px] text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {r.status === 'pending' && (
                        <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-[9px] font-bold text-yellow-400 border border-yellow-500/20">Pending</span>
                      )}
                      {r.status === 'fulfilled' && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-[9px] font-bold text-emerald-400 border border-emerald-500/20">Fulfilled</span>
                      )}
                      {r.status === 'cancelled' && (
                        <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-[9px] font-bold text-red-400 border border-red-500/20">Cancelled</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rewards Grid */}
      <div className="space-y-2.5">
        {rewards.map((reward, i) => {
          const brand = getBrand(reward.brand);
          const cheapest = reward.tiers[0]?.points || 0;
          const canAfford = userPoints >= cheapest;

          return (
            <motion.button
              key={reward.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => openReward(reward)}
              className={`w-full glass-card rounded-xl p-4 text-left hover:border-amber-500/15 transition-all ${
                !canAfford ? 'opacity-60' : ''
              }`}
              data-testid={`reward-card-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl ${reward.image_url ? '' : brand.bg} ${reward.image_url ? 'border-white/10' : brand.border} border flex items-center justify-center text-lg flex-shrink-0 overflow-hidden`}>
                  {reward.image_url ? (
                    <img src={reward.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    brand.icon
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">{reward.name}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{reward.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-mono font-bold text-amber-500">from {cheapest.toLocaleString()}</p>
                  <p className="text-[9px] text-zinc-600">pts</p>
                </div>
              </div>
              {reward.tiers.length > 0 && (
                <div className="flex gap-1.5 mt-3">
                  {reward.tiers.map((tier, ti) => (
                    <span
                      key={ti}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-bold ${
                        userPoints >= tier.points
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-white/[0.03] text-zinc-600 border border-white/5'
                      }`}
                    >
                      {tier.label}
                    </span>
                  ))}
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Redemption Dialog */}
      <Dialog open={!!selectedReward} onOpenChange={() => { setSelectedReward(null); setRedeemResult(null); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm p-0">
          {selectedReward && !redeemResult && (
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl ${selectedReward.image_url ? '' : getBrand(selectedReward.brand).bg} ${selectedReward.image_url ? 'border-white/10' : getBrand(selectedReward.brand).border} border flex items-center justify-center text-xl overflow-hidden`}>
                  {selectedReward.image_url ? (
                    <img src={selectedReward.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getBrand(selectedReward.brand).icon
                  )}
                </div>
                <div>
                  <h3 className="font-heading font-bold text-base text-white">{selectedReward.name}</h3>
                  <p className="text-xs text-zinc-500">{selectedReward.description}</p>
                </div>
              </div>

              <p className="text-xs font-bold text-zinc-400 mb-3">Select Amount</p>
              <div className="space-y-2 mb-5">
                {selectedReward.tiers.map((tier, ti) => {
                  const canAfford = userPoints >= tier.points;
                  return (
                    <button
                      key={ti}
                      disabled={!canAfford}
                      onClick={() => { setSelectedTier(ti); setConfirming(false); }}
                      className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                        selectedTier === ti
                          ? 'border-amber-500/40 bg-amber-500/10'
                          : canAfford
                            ? 'border-white/5 bg-white/[0.02] hover:border-white/10'
                            : 'border-white/5 bg-white/[0.01] opacity-50 cursor-not-allowed'
                      }`}
                      data-testid={`tier-select-${ti}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedTier === ti ? 'bg-amber-500 text-black' : 'bg-white/5 text-zinc-400'}`}>
                          {selectedTier === ti ? <Check className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                        </div>
                        <span className="text-sm font-bold text-white">{tier.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-mono font-bold text-amber-500">{tier.points.toLocaleString()}</span>
                        <span className="text-[10px] text-zinc-600 block">points</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedTier !== null && !confirming && (
                <button
                  onClick={() => setConfirming(true)}
                  className="honey-btn w-full py-3 rounded-xl text-sm font-bold"
                  data-testid="redeem-btn"
                >
                  Redeem {selectedReward.tiers[selectedTier].label} {selectedReward.name}
                </button>
              )}

              {confirming && (
                <div className="space-y-3">
                  <div className="rounded-xl p-3 border border-amber-500/20 bg-amber-500/5">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                      <p className="text-xs font-bold text-amber-400">Confirm Redemption</p>
                    </div>
                    <p className="text-[10px] text-zinc-400">
                      This will deduct <b className="text-white">{selectedReward.tiers[selectedTier].points.toLocaleString()} points</b> from your balance. This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirming(false)}
                      className="flex-1 py-2.5 rounded-xl border border-white/10 text-sm text-zinc-400 hover:text-white transition-colors"
                      data-testid="redeem-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRedeem}
                      disabled={redeeming}
                      className="flex-1 honey-btn py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
                      data-testid="redeem-confirm-btn"
                    >
                      {redeeming ? 'Processing...' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Success / Error State */}
          {redeemResult && !redeemResult.error && (
            <div className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-1">Redemption Submitted!</h3>
              <p className="text-xs text-zinc-400 mb-4">
                Your {selectedReward?.name} ({selectedReward?.tiers[selectedTier]?.label}) request is being processed.
              </p>
              <div className="glass-card rounded-lg p-3 mb-4 text-left space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Points spent</span>
                  <span className="text-amber-500 font-mono font-bold">-{redeemResult.redemption?.points_spent?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Remaining balance</span>
                  <span className="text-white font-mono font-bold">{redeemResult.new_points?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-zinc-500">Status</span>
                  <span className="text-yellow-400 font-medium">Pending fulfillment</span>
                </div>
              </div>
              <button
                onClick={() => { setSelectedReward(null); setRedeemResult(null); fetchRewards(); }}
                className="honey-btn w-full py-2.5 rounded-xl text-sm font-bold"
                data-testid="redeem-done-btn"
              >
                Done
              </button>
            </div>
          )}

          {redeemResult?.error && (
            <div className="p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <X className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-1">Redemption Failed</h3>
              <p className="text-xs text-red-400 mb-4">{redeemResult.error}</p>
              <button
                onClick={() => { setRedeemResult(null); setConfirming(false); }}
                className="honey-btn w-full py-2.5 rounded-xl text-sm font-bold"
              >
                Try Again
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
